import React from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/alert-dialog';
import { Database, Loader2, AlertTriangle, Upload } from 'lucide-react';
import { format } from 'date-fns';

export function BackupDialogs({
  createDialogOpen,
  setCreateDialogOpen,
  backupName,
  setBackupName,
  backupDescription,
  setBackupDescription,
  creating,
  onCreateBackup,
  restoreDialogOpen,
  setRestoreDialogOpen,
  selectedBackup,
  restoring,
  onRestore,
  deleteDialogOpen,
  setDeleteDialogOpen,
  onDelete,
  restoreFileDialogOpen,
  setRestoreFileDialogOpen,
  restoreFile,
  restoringFile,
  onRestoreFromFile,
  formatBytes,
}) {
  return (
    <>
      {/* Create Backup Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Database className="w-5 h-5" />
              Create Backup
            </DialogTitle>
            <DialogDescription>
              Create a manual backup of all your data. This includes customers, products, orders, batches, and settings.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Backup Name (optional)</Label>
              <Input
                value={backupName}
                onChange={(e) => setBackupName(e.target.value)}
                placeholder={`Backup ${format(new Date(), 'dd/MM/yyyy HH:mm')}`}
              />
            </div>
            <div className="space-y-2">
              <Label>Description (optional)</Label>
              <Input
                value={backupDescription}
                onChange={(e) => setBackupDescription(e.target.value)}
                placeholder="e.g., Before major update"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
            <Button onClick={onCreateBackup} disabled={creating}>
              {creating && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              <Database className="w-4 h-4 mr-2" />
              Create Backup
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Restore Confirmation Dialog */}
      <AlertDialog open={restoreDialogOpen} onOpenChange={setRestoreDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-amber-600">
              <AlertTriangle className="w-5 h-5" />
              Restore Database?
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-2">
              <p>
                <strong>Warning:</strong> This will overwrite ALL current data with the backup from:
              </p>
              <p className="font-mono bg-muted p-2 rounded">
                {selectedBackup?.name} - {selectedBackup?.created_at && format(new Date(selectedBackup.created_at), 'dd/MM/yyyy HH:mm')}
              </p>
              <p>
                A restore point will be automatically created before the operation so you can undo this if needed.
              </p>
              <p className="text-destructive font-medium">
                This action cannot be easily undone. Are you sure you want to proceed?
              </p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={onRestore}
              className="bg-amber-600 hover:bg-amber-700"
              disabled={restoring}
            >
              {restoring && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Yes, Restore Database
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Backup?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{selectedBackup?.name}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={onDelete} className="bg-destructive hover:bg-destructive/90">
              Delete Backup
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* File Restore Confirmation Dialog */}
      <AlertDialog open={restoreFileDialogOpen} onOpenChange={setRestoreFileDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-amber-600">
              <AlertTriangle className="w-5 h-5" />
              Restore from File?
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-2">
              <p>
                <strong>Warning:</strong> This will overwrite ALL current data with the contents of:
              </p>
              <p className="font-mono bg-muted p-2 rounded text-sm">
                {restoreFile?.name} ({restoreFile ? formatBytes(restoreFile.size) : ''})
              </p>
              <p>A safety restore point will be created automatically before restoring.</p>
              <p className="text-destructive font-medium">This action cannot be easily undone.</p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={restoringFile}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={onRestoreFromFile}
              disabled={restoringFile}
              className="bg-amber-600 hover:bg-amber-700"
              data-testid="btn-confirm-file-restore"
            >
              {restoringFile ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Restoring...</>
              ) : (
                <><Upload className="w-4 h-4 mr-2" /> Restore from File</>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
